// Copyright (c) 2022 by Move Closer

import { computed, ref, SetupContext } from '@vue/composition-api'

import { ComponentObjectPropsOptions } from '../../vue-api'

import { DropdownPlacement, DropdownProps } from './Dropdown.contracts'
import { DROPDOWN_POPOVER_DEFAULT_CONFIG, dropdownPlacementOptions } from './Dropdown.config'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const dropdownProps: ComponentObjectPropsOptions<DropdownProps> = {
  /**
   * @see DropdownProps.config
   */
  config: {
    type: Object,
    required: false
  },

  /**
   * @see DropdownProps.items
   */
  items: {
    type: Array,
    required: true
  },

  /**
   * @see DropdownProps.label
   */
  label: {
    type: String,
    required: true
  },

  /**
   * @see DropdownProps.placement
   */
  placement: {
    type: String,
    required: false,
    default: 'bottom-start'
  },

  container: {
    type: String,
    required: false,
    default: 'body'
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useDropdown = (props: DropdownProps, slots: SetupContext['slots']) => {
  /**
   * Determines placement of the is box.
   */
  const _placement = computed<string>(() => {
    if (!(props.placement in dropdownPlacementOptions)) {
      return dropdownPlacementOptions[DropdownPlacement.DownStart]
    }

    return dropdownPlacementOptions[props.placement]
  })

  /**
   * Constructs configuration for popover.
   */
  const popoverConfig = computed<object>(() => {
    if (typeof props.config === 'undefined') {
      return DROPDOWN_POPOVER_DEFAULT_CONFIG
    }

    return {
      ...DROPDOWN_POPOVER_DEFAULT_CONFIG,
      offset: props.config.offset || DROPDOWN_POPOVER_DEFAULT_CONFIG.offset,
      placement: _placement.value || DROPDOWN_POPOVER_DEFAULT_CONFIG.placement,
      container: props.container || DROPDOWN_POPOVER_DEFAULT_CONFIG.container
    }
  })

  /**
   * Defines the state of the dropdown.
   */
  const open = ref<boolean>(false)

  /**
   * Determines whether item slot is present in component.
   */
  const hasItemSlot = computed<boolean>(() => {
    return !!slots.item
  })

  /**
   * Toggles the state of component.
   */
  const toggle = () => {
    open.value = !open.value
  }

  return { popoverConfig, open, toggle, hasItemSlot, _placement }
}
